import { constant, noop } from 'lodash';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUnmount } from 'react-use';

import { useConnectionStatusListener } from '@air/hook-use-connection-status-listener';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getAirApiConfig } from '~/constants/airApi';
import { PENDING_SUBSCRIPTIONS, SUBSCRIPTIONS } from '~/constants/sockets';
import { createSocket } from '~/providers/SocketContext/create-socket/create-socket';
import { websocketWrapper } from '~/providers/SocketContext/proxy';
import { type SendJsonMessage, type WebSocketMessage } from '~/providers/SocketContext/shared/typings';
import { useResubscribeOnWorkspaceChange } from '~/providers/SocketContext/useResubscribeOnWorkspaceChange';
import { ReadyState } from '~/types/sockets';
import { SocketReconnectScheduler } from '~/utils/sockets/SocketReconnectScheduler';

type SendMessage = (message: WebSocketMessage, keep?: boolean) => void;

const removeAllSubscriptions = () => {
  Object.keys(SUBSCRIPTIONS).forEach((key) => {
    delete SUBSCRIPTIONS[key];
  });
  Object.keys(PENDING_SUBSCRIPTIONS).forEach((key) => {
    delete PENDING_SUBSCRIPTIONS[key];
  });
};

type SocketContextType = {
  readyState: ReadyState | null;
  getWebSocket: () => WebSocket | null;
  sendJsonMessage: SendJsonMessage;
  sendMessage: SendMessage;
};

const DEFAULT_SOCKET_CONTEXT_VALUE: SocketContextType = {
  readyState: null,
  getWebSocket: constant(null),
  sendJsonMessage: noop,
  sendMessage: noop,
};

const SocketContext = createContext<SocketContextType>(DEFAULT_SOCKET_CONTEXT_VALUE);

type SocketContextProviderProps = {
  children: ReactNode;
  connect: boolean;
  /**
   * This is the token used for authenticating with Air's WS server.
   * On private routes, this would be the auth token from Cognito and on public routes, it's the shortId
   */
  getAuthToken: () => Promise<string>;
  /**
   * This is the id of the workspace the events should be listening to
   */
  currentWorkspaceId?: string;
};

export const SocketContextProvider = memo(
  ({ children, connect, getAuthToken, currentWorkspaceId }: SocketContextProviderProps) => {
    const [readyState, setReadyStateState] = useState<ReadyState | null>(null);

    const convertedUrl = useRef<string | null>(null);
    const webSocketRef = useRef<WebSocket | null>(null);
    const startRef = useRef<() => void>(constant(void 0));
    const webSocketProxy = useRef<WebSocket | null>(null);
    const messageQueue = useRef<WebSocketMessage[]>([]);
    const expectCloseRef = useRef<boolean>(false);
    const connectRef = useRef(connect);
    const getAuthTokenRef = useRef(getAuthToken);
    const readyStateRef = useRef<ReadyState | null>(null);
    const reconnectSchedulerRef = useRef<SocketReconnectScheduler | null>(null);

    if (reconnectSchedulerRef.current === null) {
      reconnectSchedulerRef.current = new SocketReconnectScheduler();
    }

    const setReadyState = useCallback((nextReadyState: ReadyState) => {
      readyStateRef.current = nextReadyState;
      setReadyStateState(nextReadyState);
    }, []);

    connectRef.current = connect;
    getAuthTokenRef.current = getAuthToken;

    const readyStateFromUrl: ReadyState = (() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: convertedUrl is written after JWT fetch; Biome only sees the initial null.
      if (convertedUrl.current && readyState !== null) {
        return readyState;
      }

      if (connect) {
        return ReadyState.CONNECTING;
      }

      return ReadyState.UNINSTANTIATED;
    })();

    const getWebSocket = useCallback(() => {
      if (webSocketProxy.current === null && webSocketRef.current) {
        webSocketProxy.current = websocketWrapper(webSocketRef.current, startRef);
      }

      return webSocketProxy.current;
    }, []);

    const sendMessage: SendMessage = useCallback((message, keep = true) => {
      if (webSocketRef.current?.readyState === ReadyState.OPEN) {
        webSocketRef.current.send(message);
      } else if (keep) {
        messageQueue.current.push(message);
      }
    }, []);

    const sendJsonMessage: SendJsonMessage = useCallback(
      (message, keep = true) => {
        sendMessage(JSON.stringify(message), keep);
      },
      [sendMessage],
    );

    useResubscribeOnWorkspaceChange({
      isConnected: readyStateFromUrl === ReadyState.OPEN,
      sendJsonMessage,
      currentWorkspaceId,
    });

    const close = useCallback(() => {
      expectCloseRef.current = true;
      reconnectSchedulerRef.current?.cancel();
      webSocketRef.current?.close();
      setReadyState(ReadyState.CLOSED);
    }, [setReadyState]);

    const connectSocket = useCallback(async () => {
      if (!connectRef.current) {
        close();
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: expectCloseRef is set true on intentional close; Biome only sees the initial false.
      if (expectCloseRef.current) {
        return;
      }

      if (readyStateRef.current === ReadyState.CONNECTING || readyStateRef.current === ReadyState.OPEN) {
        return;
      }

      try {
        const authToken = await getAuthTokenRef.current();
        convertedUrl.current = `${getAirApiConfig().wsUrl}?authorization=${authToken}`;

        await createSocket({
          webSocketRef,
          url: convertedUrl.current,
          setReadyState,
          reconnect: () => startRef.current(),
          messageQueue,
        });
      } catch (error) {
        reportErrorToBugsnag({ error, context: `Failed to get JWT Token and create web socket.` });

        if (connectRef.current && !expectCloseRef.current) {
          startRef.current();
        }
      }
    }, [close, setReadyState]);

    const start = useCallback(() => {
      if (!connectRef.current) {
        close();
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: expectCloseRef is set true on intentional close; Biome only sees the initial false.
      if (expectCloseRef.current) {
        return;
      }

      if (readyStateRef.current === ReadyState.CONNECTING || readyStateRef.current === ReadyState.OPEN) {
        return;
      }

      reconnectSchedulerRef.current?.schedule(() => {
        connectSocket().catch((error) => {
          reportErrorToBugsnag({ error, context: 'Failed to create web socket.' });

          if (connectRef.current && !expectCloseRef.current) {
            startRef.current();
          }
        });
      });
    }, [close, connectSocket]);

    startRef.current = start;

    const resumeConnection = useCallback(() => {
      if (!connectRef.current) {
        return;
      }

      expectCloseRef.current = false;
      reconnectSchedulerRef.current?.reset();
      start();
    }, [start]);

    useConnectionStatusListener((isConnected) => {
      if (isConnected) {
        resumeConnection();
      } else {
        close();
      }
    });

    useEffect(() => {
      if (connect) {
        resumeConnection();
      } else {
        close();
      }

      return () => {
        expectCloseRef.current = true;
        reconnectSchedulerRef.current?.cancel();
        // biome-ignore lint/suspicious/noUnnecessaryConditions: the proxy is assigned in getWebSocket; Biome only sees the initial null.
        if (webSocketProxy.current) {
          webSocketProxy.current = null;
        }
      };
    }, [close, connect, resumeConnection]);

    useUnmount(() => {
      removeAllSubscriptions();
    });

    useEffect(() => {
      if (readyStateFromUrl === ReadyState.OPEN) {
        reconnectSchedulerRef.current?.reset();
      }
    }, [readyStateFromUrl]);

    useEffect(() => {
      if (readyStateFromUrl === ReadyState.OPEN) {
        messageQueue.current.splice(0).forEach((message) => {
          sendMessage(message);
        });
      }
    }, [readyStateFromUrl, sendMessage]);

    const socketValue = useMemo<SocketContextType>(
      () => ({
        readyState,
        getWebSocket,
        sendJsonMessage,
        sendMessage,
      }),
      [readyState, getWebSocket, sendJsonMessage, sendMessage],
    );

    return <SocketContext.Provider value={socketValue}>{children}</SocketContext.Provider>;
  },
);

SocketContextProvider.displayName = 'SocketContextProvider';

export const useWebsocketContext = () => {
  const socketContext = useContext(SocketContext);

  if (socketContext === DEFAULT_SOCKET_CONTEXT_VALUE) {
    console.error('Socket context used outside of its provider!');
  }

  return socketContext;
};
